import React, { useEffect, useRef, useState } from "react";

/**
 * AnimatedNumber — counter con count-up animato all'ingresso in viewport.
 * - Trigger una sola volta via IntersectionObserver (threshold 0.3)
 * - Easing: ease-out cubic
 * - Rispetta prefers-reduced-motion: se attivo, mostra direttamente il valore finale
 */
const AnimatedNumber = ({
  end,
  decimals = 0,
  duration = 2000,
  prefix = "",
  suffix = "",
  className = "",
}) => {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setValue(end);
      setStarted(true);
      return;
    }
    if (!ref.current || started) return;
    const node = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
          const startTime = performance.now();
          const tick = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(end * eased);
            if (progress < 1) requestAnimationFrame(tick);
            else setValue(end);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [end, duration, started]);

  const formatted = Number(value).toLocaleString("it-IT", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <span ref={ref} className={className} data-testid="animated-number">
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
};

export default AnimatedNumber;
